import React, { useState, useMemo, useContext, useCallback } from 'react';

// MUI
import { TextField } from '@mui/material';

// Context
import ScriptContext from '../../../context/Script';
import ProjectViewContext from '../../../context/ProjectView';

export default function DescriptionField({
  shot,
  handleShotFieldChange,
  script: scriptFromProps,
  readOnly,
  lockKey,
  placeholder = 'Describe the shot…',
  sx,
  maxLength = 2000,
  minRows = 1,
  multiline = true,
  maxRows, // optional
  inputProps, // optional merge
}) {
  const scriptCtx = useContext(ScriptContext);
  const script = scriptFromProps || scriptCtx?.script;

  const { lockedFields, lockField, unlockField } = useContext(ProjectViewContext);

  const isReadOnly = readOnly ?? script?.level === 'viewer';
  const key = lockKey || `shot-${shot?.id}-description`;
  const descriptionLocked = isReadOnly || lockedFields?.has?.(key);

  const [tempDescriptions, setTempDescriptions] = useState({});

  const descriptionValue = useMemo(() => {
    const sid = shot?.id;
    if (!sid) return shot?.shot_description || '';
    return tempDescriptions[sid] !== undefined
      ? tempDescriptions[sid]
      : shot?.shot_description || '';
  }, [shot?.id, shot?.shot_description, tempDescriptions]);

  const commit = useCallback(
    async (shotId) => {
      if (!shotId) return;
      if (tempDescriptions[shotId] === undefined) return;

      await handleShotFieldChange?.(shotId, 'shot_description', tempDescriptions[shotId]);

      setTempDescriptions((prev) => ({
        ...prev,
        [shotId]: undefined,
      }));
    },
    [handleShotFieldChange, tempDescriptions],
  );

  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Description"
      size="small"
      value={descriptionValue}
      disabled={descriptionLocked}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      maxRows={multiline ? maxRows : undefined}
      inputProps={{ maxLength, ...(inputProps || {}) }}
      sx={{ bgcolor: 'background.paper', ...(sx || {}) }}
      placeholder={placeholder}
      InputLabelProps={{ shrink: true }}
      onFocus={() => {
        if (typeof lockField === 'function') lockField(key);
      }}
      onChange={(e) =>
        setTempDescriptions((prev) => ({
          ...prev,
          [shot?.id]: e.target.value,
        }))
      }
      onBlur={async () => {
        await commit(shot?.id);
        if (typeof unlockField === 'function') unlockField(key);
      }}
    />
  );
}
